/* oxlint-disable @typescript-eslint/no-explicit-any */
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { AccessOverviewEditChartColorsElementEnum, AccessSummaryEditChartColorsElementEnum } from "@app/access-reports/admin";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard";
import {
  AccessReportParametersModel,
  AccessReportsWizardStep,
  EmployeeGroupExample,
} from "@app/access-reports/wizard";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { assignTemplateMultiElementOptions, getElementByIdFromTemplate, getFormGroupByGroupPropertyIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { ProviderSetVersionModel } from "@app/provider-sets/providerSet";
import { cloneDeep, flatten, isEqual } from "lodash";
import { renderToStaticMarkup } from "react-dom/server";
import React, { FC, JSX, useEffect, useRef, useState } from "react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import ProviderSetService from "@app/provider-sets/providerSetService";
import ServiceAreaService from "@app/service-areas/serviceAreaService";
import { ServiceAreaVersionModel } from "@app/service-areas/serviceAreaVersionModel";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportSummary } from "@app/access-reports/dashboard/AccessReportSummary";
import { AccessListingPage } from "@app/access-reports/shared/accessListingPage";
import { AccessOverviewPage } from "@app/access-reports/shared/accessOverviewPage";
import { AccessRadiusSearchPage } from "@app/access-reports/shared/accessRadiusSearchPage";
import { AccessRadiusSearchPageElementEnum } from "@app/access-reports/shared/accessRadiusSearchPageElementEnum";
import { AccessReport } from "@app/access-reports/shared/accessReport";
import { AccessReportsAccessAnalysisElementEnum } from "@app/access-reports/shared/accessReportsAccessAnalysisElementEnum";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsPageOptionsElementEnum } from "@app/access-reports/shared/accessReportsPageOptionsElementEnum";
import { AccessReportsPrintOptions } from "@app/access-reports/shared/accessReportsPrintOptions";
import { AccessReportsProductDefaultElementsEnum } from "@app/access-reports/shared/accessReportsProductDefaultElementsEnum";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardAnalysisTypeEnum } from "@app/access-reports/shared/accessReportsWizardAnalysisTypeEnum";
import { AccessReportsWizardDetailsPage } from "@app/access-reports/shared/accessReportsWizardDetailsPage";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardEmployeeCountPage } from "@app/access-reports/shared/accessReportsWizardEmployeeCountPage";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardGroupPropertyIdEnum } from "@app/access-reports/shared/accessReportsWizardGroupPropertyIdEnum";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardProviderCountPage } from "@app/access-reports/shared/accessReportsWizardProviderCountPage";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardSummaryPage } from "@app/access-reports/shared/accessReportsWizardSummaryPage";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";
import { ByProductDefault } from "@app/access-reports/shared/byProductDefault";
import { ColumnDefaults } from "@app/access-reports/shared/columnDefaults";
import { EmployeeGroup } from "@app/access-reports/shared/employeeGroup";
import { EmployeeGroupTypeEnum } from "@app/access-reports/shared/employeeGroupTypeEnum";
import { PageSelectionElementIdEnum } from "@app/access-reports/shared/pageSelectionElementIdEnum";
import { SelectedColumnsByFilter } from "@app/access-reports/shared/selectedColumnsByFilter";
import { AccessReportsWizardForm } from "@app/access-reports/wizard/AccessReportsWizardForm";
import { AccessReportsWizardStepAccessAnalysis } from "@app/access-reports/wizard/AccessReportsWizardStepAccessAnalysis";
import { AccessReportsWizardStepPopulation } from "@app/access-reports/wizard/AccessReportsWizardStepPopulation";
import { CreateAccessReportsBulkModel } from "@app/access-reports/wizard/shared/createAccessReportsBulkModel";

type AccessReportsWizardProps = {
  clientId: number;
  email: string;
}

interface RouteState {
  accessReportId: number;
}

const getCleanAccessReportParameters = (existingParameters?: AccessReportParametersModel): AccessReportParametersModel => {
  const newAccessReportParameters: AccessReportParametersModel = {
    /* oxlint-disable sort-keys */
    id: 0,

    // product
    productType: null,
    product: null,
    standard: null,

    // calculation method
    calculationMethod: null,
    distanceMethod: null,

    // logo
    logo: null,

    // company information
    companyName: "",
    requestorName: "",
    internalClientId: "",

    // cover page information
    reportTitleOne: "",
    reportTitleTwo: "",
    reportTitleThree: "",

    // created by
    createdByOne: "",
    createdByTwo: "",
    createdByThree: "",

    // email options
    accessSharedOutput: {
      bodyText: "",
      cc: "",
      to: "",
    },

    // page options
    splitPagesBySpecialty: false,
    splitEmployeeGroupByStandard: false,
    coverPage: false,
    contentsPage: false,
    separatorPage: false,
    legalDisclaimer: "",
    accessReportsWizardDetailsPages: [],
    accessReportsWizardSummaryPages: [],
    employeeCountPages: [],
    accessOverviewPages: [],
    accessListingPages: [],
    accessRadiusSearchPages: [],
    providerCountPages: [],

    // print options
    printOptions: null,
    /* oxlint-enable sort-keys */
  };

  // preserve any non-by-product values if getting clean model after original page load,
  // since this should only happen when setting/reseting product defaults
  if (!!existingParameters) {
    for (const key of Object.keys(newAccessReportParameters)) {
      if (!Object.values(AccessReportsProductDefaultElementsEnum).includes(key as any) && !Object.values(AccessReportsWizardGroupPropertyIdEnum).includes(key as any)) {
        (newAccessReportParameters as any)[key] = (existingParameters as any)[key];
      }
    }
  }

  return newAccessReportParameters;
}

const AccessReportsWizard: FC<AccessReportsWizardProps> = (props: AccessReportsWizardProps) => {
  const accessReportsService = useRef(new AccessReportsService()).current;
  const providerSetService = useRef(new ProviderSetService()).current;
  const serviceAreaService = useRef(new ServiceAreaService()).current;
  const previousAccessReportParameters = useRef<AccessReportParametersModel>(null);
  const previousIsClone = useRef<boolean>(false);

  const routeMatch = useRouteMatch();
  const history = useHistory();
  const location = useLocation<RouteState>();

  const [accessReportParameters, setAccessReportParameters] = useState<AccessReportParametersModel>(getCleanAccessReportParameters());
  const [accessReportsWizardTemplate, setAccessReportsWizardTemplate] = useState<AccessReportsWizardTemplate>(null);
  const [employeeGroup, setEmployeeGroup] = useState<EmployeeGroup>(null);
  const [employeeGroupExamples, setEmployeeGroupExamples] = useState<EmployeeGroupExample[]>([]);
  const [employeeGroupUploadMessage, setEmployeeGroupUploadMessage] = useState<string>(null);
  const [employeeGroupUploadStatus, setEmployeeGroupUploadStatus] = useState<AccessReportStatusEnum>(null);
  const [formChanged, setFormChanged] = useState(false);
  const [invalidElements, setInvalidElements] = useState(new Map<string, string>());
  const [invalidPageOptionPageElements, setInvalidPageOptionPageElements] = useState(new Map<string, string>());
  const [isClone, setIsClone] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [originalAccessReportsWizardTemplate, setOriginalAccessReportsWizardTemplate] = useState<AccessReportsWizardTemplate>(null);
  const [productChanged, setProductChanged] = useState(false);
  const [repeatedByProductDefaultIds, setRepeatedByProductDefaultIds] = useState<number[]>([]);
  const [showProductDoesntExistBanner, setShowProductDoesntExistBanner] = useState(false);

  useEffect(() => {
    fetchAccessReportsWizardTemplateAndModel();
    getEmployeeGroupExamples();
  }, []);

  // if a product type is selected, and is different specifically due to a form change,
  // then all by-product defaults should be reset
  useEffect(() => {
    if (!!accessReportParameters.productType
      && !isEqual(previousAccessReportParameters.current.productType, accessReportParameters.productType)
      && formChanged) {
      resetDefaultsForNewProductType();
    }
  }, [accessReportParameters.productType, formChanged]);

  // if a product is selected, and is different specifically due to a form change,
  // then by-product defaults should be set for the new product
  useEffect(() => {
    if (!!accessReportParameters.product
      && previousAccessReportParameters.current.product?.id !== accessReportParameters.product.id
      && formChanged) {
      setDefaultsByProduct();
    }
  }, [accessReportParameters.product, accessReportParameters.product?.id, formChanged]);

  // if the product is the same, but the standard is different specifically due to a form change,
  // then update "split employee group by standard" settings
  useEffect(() => {
    if (!!accessReportParameters.product
      && previousAccessReportParameters.current.product?.id !== accessReportParameters.product.id
      && !isEqual(previousAccessReportParameters.current.standard, accessReportParameters.standard)
      && formChanged
      && !productChanged) {
      updateSplitEmployeeGroupByStandardSettings();
    }
  }, [accessReportParameters.product, accessReportParameters.product?.id, accessReportParameters.standard, formChanged, productChanged]);

  // if the product is the same, but a count page was added specifically due to a form change,
  // then update "count" settings
  useEffect(() => {
    if (!!accessReportParameters.product
      && isEqual(previousAccessReportParameters.current.product, accessReportParameters.product)
      && formChanged
      && !productChanged) {
      // if a provider count page was added
      if (accessReportParameters.providerCountPages.length > previousAccessReportParameters.current.providerCountPages.length) {
        updateCountSettings([AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages]);
        previousAccessReportParameters.current.providerCountPages.length = accessReportParameters.providerCountPages.length;
      }

      // if an employee count page was added
      if (accessReportParameters.employeeCountPages.length > previousAccessReportParameters.current.employeeCountPages.length) {
        updateCountSettings([AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage]);
        previousAccessReportParameters.current.employeeCountPages.length = accessReportParameters.employeeCountPages.length;
      }
    }
  }, [accessReportParameters.product, formChanged, productChanged]);

  // the conditions for a product change are met either on load if it's a clone,
  // or directly after by-product defaults have been set, in which case both the
  // "split employee group by standard" and "employee count" settings should be updated
  useEffect(() => {
    if (previousIsClone.current !== isClone || productChanged) {
      updateSplitEmployeeGroupByStandardSettings();
      updateCountSettings([AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages, AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage]);
    }
  }, [isClone, productChanged]);

  const createPage = (groupProperty: AccessReportsWizardGroupPropertyIdEnum, id: number, selected: boolean, template: AccessReportsWizardTemplate = accessReportsWizardTemplate):
    AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessOverviewPage | AccessListingPage | AccessRadiusSearchPage | AccessReportsWizardProviderCountPage => {
    // create a blank new page for the currently-selected product with only admin-level properties defaulted
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupProperty);
    let page: AccessReportsWizardSummaryPage | AccessReportsWizardDetailsPage | AccessReportsWizardEmployeeCountPage | AccessOverviewPage | AccessListingPage | AccessRadiusSearchPage | AccessReportsWizardProviderCountPage;

    const setChartColors = (
      chartColorIds: AccessSummaryEditChartColorsElementEnum[] | AccessOverviewEditChartColorsElementEnum[],
      setPage?: AccessReportsWizardSummaryPage | AccessOverviewPage,
    ) => {
      for (const element of pageFormGroup.elements) {
        if ((chartColorIds as string[]).includes(element.elementId)) {
          if (!!setPage) {
            (page as any)[element.elementId] = (setPage as any)[element.elementId];
          } else {
            if (!!element.defaultValue) {
              if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                const option: AccessReportsWizardOption = element.options.find((x) => x.id.toString() === element.defaultValue?.toString());

                if (!!option) {
                  (page as any)[element.elementId] = option;
                }
              } else {
                (page as any)[element.elementId] = element.defaultValue;
              }
            }
          }
        }
      }
    }

    switch (groupProperty) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        page = new AccessReportsWizardSummaryPage(selected, id);
        setChartColors(Object.values(AccessSummaryEditChartColorsElementEnum), accessReportParameters.accessReportsWizardSummaryPages[0]);

        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
        page = new AccessReportsWizardDetailsPage(selected, id);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
        page = new AccessReportsWizardEmployeeCountPage(selected, id);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        page = new AccessOverviewPage(selected, id);
        setChartColors(Object.values(AccessOverviewEditChartColorsElementEnum), accessReportParameters.accessOverviewPages[0]);

        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        page = new AccessListingPage(selected, id);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        page = new AccessRadiusSearchPage(selected, id);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        page = new AccessReportsWizardProviderCountPage(selected, id);
        break;
      default:
        break;
    }

    return page;
  }

  const onAddPageClick = (groupProperty: AccessReportsWizardGroupPropertyIdEnum): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = accessReportsWizardTemplate;
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newAccessReportsWizardTemplate, AccessReportsWizardStepEnum.PageOptions, groupProperty);
    const id: number = pageFormGroup.byProductDefaults.length > 0 ? Math.max(...pageFormGroup.byProductDefaults.map((x) => x.id)) + 1 : 0;

    const page = createPage(groupProperty, id, true);

    (newAccessReportParameters[groupProperty] as any).push(page);

    (page as any).productId = newAccessReportParameters.product.id;
    const byProductDefault: ByProductDefault = page as any;
    pageFormGroup.byProductDefaults.push(byProductDefault)

    const newRepeatedByProductDefaultIds: number[] = cloneDeep(repeatedByProductDefaultIds);
    newRepeatedByProductDefaultIds.push(byProductDefault.id);

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
    setRepeatedByProductDefaultIds(newRepeatedByProductDefaultIds);
  }

  const onRemovePageClick = (groupProperty: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = cloneDeep(accessReportsWizardTemplate);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newAccessReportsWizardTemplate, AccessReportsWizardStepEnum.PageOptions, groupProperty);
    pageFormGroup.byProductDefaults = pageFormGroup.byProductDefaults.filter(x => x.id !== byProductDefaultId);

    switch (groupProperty) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        newAccessReportParameters.accessReportsWizardSummaryPages = newAccessReportParameters.accessReportsWizardSummaryPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
        newAccessReportParameters.accessReportsWizardDetailsPages = newAccessReportParameters.accessReportsWizardDetailsPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
        newAccessReportParameters.employeeCountPages = newAccessReportParameters.employeeCountPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        newAccessReportParameters.accessOverviewPages = newAccessReportParameters.accessOverviewPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
        newAccessReportParameters.accessListingPages = newAccessReportParameters.accessListingPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
        newAccessReportParameters.accessRadiusSearchPages = newAccessReportParameters.accessRadiusSearchPages.filter((x) => x.id !== byProductDefaultId);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
        newAccessReportParameters.providerCountPages = newAccessReportParameters.providerCountPages.filter((x) => x.id !== byProductDefaultId);
        break;
      default:
        break;
    }

    const newRepeatedByProductDefaultIds: number[] = repeatedByProductDefaultIds.filter((x) => x !== byProductDefaultId);

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
    setRepeatedByProductDefaultIds(newRepeatedByProductDefaultIds);
  }

  const getDisabledStatus = (formGroup: AccessReportsWizardFormGroup, element: AccessReportsWizardElement, byProductDefaultId?: number): boolean => {
    // an element can be disabled:
    // a.) through the json, which can be toggled through the access reports admin
    // b.) if it is part of a "page" form group that has been unselected
    // c.) if an element that is required prior to accessing it does not have a value
    // d.) if an element that is required prior to accessing it does not have a SPECIFIC value

    // an element that will always be disabled, as specified by the aforementioned json
    if (element.disabled) {
      return true;
    }

    // if the element's accessibility depends on a previous form element's value
    if (!!element.previousRequiredElementId) {
      // if the element's values must be accessed using grouped model logic for "pages"
      if (!!formGroup.groupPropertyId) {
        const formGroupModel = (accessReportParameters as any)[formGroup.groupPropertyId]?.find((x: any) => (x.id === byProductDefaultId));

        if (!!formGroupModel) {
          const pageEnumSelectionKey: string = Object.keys(PageSelectionElementIdEnum).find(x => x.toUpperCase() === formGroup.groupPropertyId.toUpperCase());
          const pageSelectionKey = (PageSelectionElementIdEnum as any)[pageEnumSelectionKey];

          // if an access page is not checked, then all elements in the form group should be disabled
          if (!formGroupModel[pageSelectionKey]) {
            return true;
          }

          // if the previous form element's value must be a specific one
          if (element.previousRequiredValueId !== null && element.previousRequiredValueId !== undefined) {
            if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
              if (formGroupModel[element.previousRequiredElementId] && (formGroupModel[element.previousRequiredElementId].id.toString() !== element.previousRequiredValueId.toString())) {
                return true;
              }
            } else {
              if (formGroupModel[element.previousRequiredElementId] && (formGroupModel[element.previousRequiredElementId] !== element.previousRequiredValueId)) {
                return true;
              }
            }
          } else {
            // if the previous form element's value doesn't matter, as long as a value is indeed present
            if (formGroupModel[element.previousRequiredElementId] === null || formGroupModel[element.previousRequiredElementId] === undefined || formGroupModel[element.previousRequiredElementId] === false || formGroupModel[element.previousRequiredElementId] === "") {
              return true;
            }
          }

          // if the previously required element is disabled, then the one's succeeding it should be disabled as well
          const previousElement: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

          if (previousElement && previousElement.previousRequiredElementId) {
            // if the previous form element's previous required value must be a specific one
            if (previousElement.previousRequiredValueId !== null && previousElement.previousRequiredValueId !== undefined) {
              if (previousElement.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                if (formGroupModel[previousElement.previousRequiredElementId] && (formGroupModel[previousElement.previousRequiredElementId].id.toString() !== previousElement.previousRequiredValueId.toString())) {
                  return true;
                }
              } else {
                if (formGroupModel[previousElement.previousRequiredElementId] && (formGroupModel[previousElement.previousRequiredElementId] !== previousElement.previousRequiredValueId)) {
                  return true;
                }
              }
            } else {
              // if the previous form element's previous required value doesn't matter, as long as a value is indeed present
              if (formGroupModel[previousElement.previousRequiredElementId] === null || formGroupModel[previousElement.previousRequiredElementId] === undefined || formGroupModel[previousElement.previousRequiredElementId] === false) {
                return true;
              }
            }
          }
        }
      } else {
        // regularly accessing an element's values
        if (element.previousRequiredValueId !== null && element.previousRequiredValueId !== undefined) {
          // if the previous form element's value must be a specific one
          if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
            if ((accessReportParameters as any)[element.previousRequiredElementId] && ((accessReportParameters as any)[element.previousRequiredElementId].id.toString() !== element.previousRequiredValueId.toString())) {
              return true;
            }
          } else {
            if ((accessReportParameters as any)[element.previousRequiredElementId] && ((accessReportParameters as any)[element.previousRequiredElementId] !== element.previousRequiredValueId)) {
              return true;
            }
          }
        } else {
          // if the previous form element's value doesn't matter, as long as a value is indeed present
          if ((accessReportParameters as any)[element.previousRequiredElementId] === null || (accessReportParameters as any)[element.previousRequiredElementId] === undefined || (accessReportParameters as any)[element.previousRequiredElementId] === false) {
            return true;
          }
        }

        // if the previously required element is disabled, then the one's succeeding it should be disabled as well
        if (!!formGroup) {
          const previousElement: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === element.previousRequiredElementId);

          if (!!previousElement && !!previousElement.previousRequiredElementId) {
            // if the previous form element's previous required value must be a specific one
            if (previousElement.previousRequiredValueId !== null && previousElement.previousRequiredValueId !== undefined) {
              if (previousElement.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                if ((accessReportParameters as any)[previousElement.previousRequiredElementId] && ((accessReportParameters as any)[previousElement.previousRequiredElementId].id.toString() !== previousElement.previousRequiredValueId.toString())) {
                  return true;
                }
              } else {
                if ((accessReportParameters as any)[previousElement.previousRequiredElementId] && ((accessReportParameters as any)[previousElement.previousRequiredElementId] !== previousElement.previousRequiredValueId)) {
                  return true;
                }
              }

            } else {
              // if the previous form element's previous required value doesn't matter, as long as a value is indeed present
              if ((accessReportParameters as any)[previousElement.previousRequiredElementId] === null || (accessReportParameters as any)[previousElement.previousRequiredElementId] === undefined || (accessReportParameters as any)[previousElement.previousRequiredElementId] === false) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  const onAccessCriteriaCheckboxToggle = (selectedKeys: string[], selectedGroupKey: string): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);

    if (!!newAccessReportParameters && !!newAccessReportParameters.product && !!newAccessReportParameters.product.accessCriterion) {
      for (const accessCriteria of newAccessReportParameters.product.accessCriterion) {
        if (selectedKeys.includes(accessCriteria.id.toString()) || (accessCriteria.groups && accessCriteria.groups.includes(selectedGroupKey))) {
          accessCriteria.selected = true;
        } else {
          accessCriteria.selected = false;
        }
      }

      previousAccessReportParameters.current = accessReportParameters;
      setAccessReportParameters(newAccessReportParameters);
    }
  }

  const onCheckboxChange = (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, byProductDefaultId?: number): void => {
    const newAccessReportParameters = cloneDeep(accessReportParameters) as any;
    const page: AccessReportsWizardPage = getPageFromTemplate(accessReportsWizardTemplate, wizardStep);
    const formGroup: AccessReportsWizardFormGroup = page?.formGroups.find((x) => x.id.toString() === formGroupId.toString());

    if (!!formGroup?.groupPropertyId) {
      const formGroupModels = newAccessReportParameters[formGroup.groupPropertyId];
      const formGroupModel = formGroupModels?.find((x: any) => (x.id === byProductDefaultId));

      if (!!formGroupModel) {
        formGroupModel[key] = !formGroupModel[key];
      }
    } else {
      newAccessReportParameters[key] = !newAccessReportParameters[key];
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setFormChanged(true);
  }

  const onGroupedAccessCriterionClick = (groupKey: string, selectedKeys: string[]): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);

    for (const accessCriteria of newAccessReportParameters.product.accessCriterion) {
      if ((!!accessCriteria.groups && accessCriteria.groups.includes(groupKey)) || selectedKeys.includes(accessCriteria.id.toString())) {
        accessCriteria.selected = true;
      } else {
        accessCriteria.selected = false;
      }
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
  }

  const handleAccessListingPageInputChange = (
    newAccessReportParameters: any,
    formGroupModel: any,
    key: keyof AccessReportParametersModel,
    event?: React.ChangeEvent<HTMLInputElement>,
    currentValue?: string,
  ): void => {
    if (!!event) {
      // on change
      const unparsedValue: string = event.target.value;
      let value: any = parseFloat(event.target.value);

      if (key.toUpperCase().includes("MILE")) {
        const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

        if (re.test(unparsedValue)) {
          if (value >= 1) {
            // remove leading zeros
            value = unparsedValue.replace(/^0+/, "");
          }

          if ((!isNaN(value) && value >= 1 && value <= 9999.9) || !unparsedValue || unparsedValue === ".") {
            formGroupModel[key] = (!unparsedValue || unparsedValue === ".") ? unparsedValue : value;
          }

        }
      } else {
        let minValue = 0;
        let maxValue = 999;
        if (key.toUpperCase().includes("MINUTE")) {
          minValue = 1;
          maxValue = 9999;
        }

        if ((!isNaN(value) && value >= minValue && value <= maxValue) || !unparsedValue) {
          formGroupModel[key] = !unparsedValue ? unparsedValue : value;
        }
      }
    } else {
      // on blur
      if (key.toUpperCase().includes("COUNT")) {
        if (!currentValue || currentValue === "") {
          formGroupModel[key] = "0";
        }
      } else {
        if (!currentValue || currentValue === "" || currentValue === "0" || currentValue === "." || currentValue === "0.") {
          formGroupModel[key] = "1";
        }
      }
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setFormChanged(true);
  }

  const handleAccessRadiusSearchPageChage = (
    newAccessReportParameters: any,
    formGroupModel: any,
    key: keyof AccessReportParametersModel,
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const unparsedValue: string = event.target.value;
    let value: any = parseFloat(event.target.value);
    const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

    if (re.test(unparsedValue)) {
      if (value >= 1) {
        // remove leading zeros
        value = unparsedValue.replace(/^0+/, "");
      }

      if ((!isNaN(value) && value >= 1 && value <= 999.9) || !unparsedValue) {
        formGroupModel[key] = !unparsedValue ? unparsedValue : value;
        const newInvalidPageOptionPageElements: Map<string, string> = cloneDeep(invalidPageOptionPageElements);

        const getElementValue = (elementId: string): number => {
          return parseFloat((formGroupModel as any)[elementId].toString());
        }

        const handleMapSetting = (mapElementId: string, isInvalid: boolean): void => {
          const mappingKey: string = (formGroupModel as AccessReportsWizardFormGroup).id + mapElementId;
          const alreadyExistingMapping: string = newInvalidPageOptionPageElements.get(mappingKey);

          if (isInvalid && !alreadyExistingMapping) {
            newInvalidPageOptionPageElements.set(mappingKey, "Radii values must be ascending");
          } else if (!!alreadyExistingMapping && !isInvalid) {
            newInvalidPageOptionPageElements.delete(mappingKey);
          }
        }

        const radiiOne: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiOne);
        const radiiTwo: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiTwo);
        const radiiThree: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiThree);
        const radiiFour: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFour);
        const radiiFive: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFive);

        let radiiOneInvalid = false;
        let radiiTwoInvalid = false;
        let radiiThreeInvalid = false;
        let radiiFourInvalid = false;
        let radiiFiveInvalid = false;


        if (radiiOne >= radiiTwo || radiiOne >= radiiThree || radiiOne >= radiiFour || radiiOne >= radiiFive) {
          radiiOneInvalid = true;
        }

        if (radiiTwo <= radiiOne || radiiTwo >= radiiThree || radiiTwo >= radiiFour || radiiTwo >= radiiFive) {
          radiiTwoInvalid = true;
        }

        if (radiiThree <= radiiOne || radiiThree <= radiiTwo || radiiThree >= radiiFour || radiiThree >= radiiFive) {
          radiiThreeInvalid = true;
        }

        if (radiiFour <= radiiOne || radiiFour <= radiiTwo || radiiFour <= radiiThree || radiiFour >= radiiFive) {
          radiiFourInvalid = true;
        }

        if (radiiFive <= radiiOne || radiiFive <= radiiTwo || radiiFive <= radiiThree || radiiFive <= radiiFour) {
          radiiFiveInvalid = true;
        }

        handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiOne, radiiOneInvalid);
        handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiTwo, radiiTwoInvalid);
        handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiThree, radiiThreeInvalid);
        handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFour, radiiFourInvalid);
        handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFive, radiiFiveInvalid);

        previousAccessReportParameters.current = accessReportParameters;
        setAccessReportParameters(newAccessReportParameters);
        setFormChanged(true);
        setInvalidPageOptionPageElements(newInvalidPageOptionPageElements);
      }
    }
  }

  const handlePagesInputChange = (
    accessReportParameters: any,
    byProductDefaultId: number,
    groupPropertyId: AccessReportsWizardGroupPropertyIdEnum,
    key: keyof AccessReportParametersModel,
    event?: React.ChangeEvent<HTMLInputElement>,
    currentValue?: string,
  ): void => {
    const formGroupModels: any[] = accessReportParameters[groupPropertyId];
    const formGroupModel: any = formGroupModels?.find((x: any) => (x.id === byProductDefaultId));

    if (!!formGroupModel) {
      switch (groupPropertyId) {
        case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
          handleAccessListingPageInputChange(accessReportParameters, formGroupModel, key, event, currentValue);
          break;
        case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
          if (!!event) {
            handleAccessRadiusSearchPageChage(accessReportParameters, formGroupModel, key, event);
          }

          break;
        default:
          break;
      }
    }
  }

  const onInputChange = (
    wizardStep: AccessReportsWizardStepEnum,
    formGroupId: number,
    key: any,
    byProductDefaultId?: number,
    event?: React.ChangeEvent<HTMLInputElement>,
    currentValue?: string,
  ): void => {
    const newAccessReportParameters = cloneDeep(accessReportParameters) as any;
    const newInvalidElements: Map<string, string> = invalidElements;
    const page: AccessReportsWizardPage = accessReportsWizardTemplate?.pages?.find((x) => x.title === wizardStep);
    const formGroup: AccessReportsWizardFormGroup = page?.formGroups.find((x) => x.id === formGroupId);

    if (!!formGroup?.groupPropertyId) {
      // handle input changes differently for certain "pages"
      handlePagesInputChange(newAccessReportParameters, byProductDefaultId, formGroup.groupPropertyId, key, event, currentValue);
    } else {
      if (!!event) {
        // on change
        if (key === AccessReportsCoverPageElementEnum.To) {
          newAccessReportParameters.accessSharedOutput.to = event.target.value;
        } else {
          newAccessReportParameters[key] = event.target.value;
        }

        const inputsWithValidation: AccessReportsCoverPageElementEnum[] = [
          AccessReportsCoverPageElementEnum.ReportTitleOne,
          AccessReportsCoverPageElementEnum.ReportTitleTwo,
          AccessReportsCoverPageElementEnum.ReportTitleThree,
          AccessReportsCoverPageElementEnum.To,
        ];

        // check validity of certain inputs to prompt specific red error text
        if (inputsWithValidation.includes(key)) {
          let invalidString;
          let isValid = false;

          switch (key) {
            case AccessReportsCoverPageElementEnum.ReportTitleOne:
            case AccessReportsCoverPageElementEnum.ReportTitleTwo:
            case AccessReportsCoverPageElementEnum.ReportTitleThree:
              invalidString = `Your report title may not contain any of the following characters: \u002f \u005c \u003a \u002e \u002a \u003f \u0022 \u003c \u003e |`;
              isValid = checkReportTitleValidity(event.target.value);
              break;
            case AccessReportsCoverPageElementEnum.To:
              invalidString = `Please enter a valid email address or addresses`;
              isValid = checkEmailToValidity(event.target.value);
              break;
            default:
              break;
          }

          const alreadyExistingMapping: string = newInvalidElements.get(key);

          if (isValid) {
            if (!!alreadyExistingMapping) {
              newInvalidElements.delete(key);
            }
          } else {
            if (!alreadyExistingMapping) {
              newInvalidElements.set(key, invalidString);
            }
          }
        }

        previousAccessReportParameters.current = accessReportParameters;
        setAccessReportParameters(newAccessReportParameters);
        setFormChanged(true);
        setInvalidElements(newInvalidElements);
      }
      // at the moment, nothing needs to occur on blur for non-"page" inputs
    }
  }

  const onProductChange = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);

    if (!!newAccessReportParameters && !!newAccessReportParameters.product) {
      const index: number = newAccessReportParameters.product.accessCriterion.map((x) => x.id).indexOf(accessCriteria.id);

      if (index !== -1) {
        newAccessReportParameters.product.accessCriterion[index] = accessCriteria;

        previousAccessReportParameters.current = accessReportParameters;
        setAccessReportParameters(newAccessReportParameters);
      }
    }
  }

  const onSelectChange = (wizardStep: AccessReportsWizardStepEnum, formGroupId: number, key: keyof AccessReportParametersModel, event: React.ChangeEvent<HTMLSelectElement>, byProductDefaultId?: number): void => {
    const newAccessReportParameters = cloneDeep(accessReportParameters) as any;

    const page: AccessReportsWizardPage = getPageFromTemplate(accessReportsWizardTemplate, wizardStep);
    const formGroup: AccessReportsWizardFormGroup = page?.formGroups.find((x) => x.id.toString() === formGroupId.toString());
    const element: AccessReportsWizardElement = formGroup?.elements.find((x) => x.elementId === key);
    const option: AccessReportsWizardOption = element?.options.find((x) => x.id.toString() === event.target.value);

    if (!!option) {
      const optionClone: AccessReportsWizardOption = cloneDeep(option);

      if (!!formGroup.groupPropertyId) {
        const formGroupModels = newAccessReportParameters[formGroup.groupPropertyId];
        const formGroupModel = formGroupModels?.find((x: any) => (x.id === byProductDefaultId));

        if (!!formGroupModel) {
          formGroupModel[key] = optionClone;
        }
      } else {
        newAccessReportParameters[key] = optionClone;
      }
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setFormChanged(true);
  }

  const onSubmitClick = async (): Promise<void> => {
    const createAccessReportsBulkModel: CreateAccessReportsBulkModel = {
      accessReports: [accessReportParameters].map((m) => {
        return {
          parameters: m,
        };
      }),
    };

    if (!!createAccessReportsBulkModel.accessReports && createAccessReportsBulkModel.accessReports.length > 0) {
      const printOptions: AccessReportsPrintOptions = {
        bodyFont: "",
        dateFormat: "",
        datePicker: null,
        dateType: "",
        headerColor: "",
        headerFont: "",
        noteFont: "",
        pageOrientation: "",
        pageSize: "",
        shadingColor: "",
        showDistanceMethod: false,
        showPageNumbers: false,
        startPage: null,
        titleFont: "",
      };

      const printOptionsPage: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);

      if (!!printOptionsPage) {
        for (const formGroup of printOptionsPage.formGroups) {
          for (const element of formGroup.elements) {
            if (element.elementId in printOptions && element.defaultValue !== undefined && element.defaultValue !== null) {
              if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                const defaultOption: AccessReportsWizardOption = element.options.find((x) => x.id.toString() === element.defaultValue.toString());

                if (!!defaultOption) {
                  (printOptions as any)[element.elementId] = defaultOption.name;
                }
              } else {
                (printOptions as any)[element.elementId] = element.defaultValue;
              }
            }
          }
        }
      }

      for (const model of createAccessReportsBulkModel.accessReports) {
        const parameters = model.parameters;

        // extract any unselected pages
        if (!!parameters.accessReportsWizardDetailsPages) {
          for (const detailPage of parameters.accessReportsWizardDetailsPages) {
            if (!detailPage.accessDetail) {
              parameters.accessReportsWizardDetailsPages = parameters.accessReportsWizardDetailsPages.filter((x) => x.id !== detailPage.id);
            }
          }
        }

        if (!!parameters.accessReportsWizardSummaryPages) {
          for (const summaryPage of parameters.accessReportsWizardSummaryPages) {
            if (!summaryPage.accessSummary) {
              parameters.accessReportsWizardSummaryPages = parameters.accessReportsWizardSummaryPages.filter((x) => x.id !== summaryPage.id);
            }
          }
        }

        if (!!parameters.employeeCountPages) {
          for (const employeeCountPage of parameters.employeeCountPages) {
            if (!employeeCountPage.employeeCount) {
              parameters.employeeCountPages = parameters.employeeCountPages.filter((x) => x.id !== employeeCountPage.id);
            }
          }
        }

        if (!!parameters.accessOverviewPages) {
          for (const overviewPage of parameters.accessOverviewPages) {
            if (!overviewPage.accessOverview) {
              parameters.accessOverviewPages = parameters.accessOverviewPages.filter((x) => x.id !== overviewPage.id);
            }
          }
        }

        if (!!parameters.accessListingPages) {
          for (const listingPage of parameters.accessListingPages) {
            if (!listingPage.accessListing) {
              parameters.accessListingPages = parameters.accessListingPages.filter((x) => x.id !== listingPage.id);
            }
          }
        }

        if (!!parameters.accessRadiusSearchPages) {
          for (const radiusSearchPage of parameters.accessRadiusSearchPages) {
            if (!radiusSearchPage.accessRadiusSearch) {
              parameters.accessRadiusSearchPages = parameters.accessRadiusSearchPages.filter((x) => x.id !== radiusSearchPage.id);
            }
          }
        }

        if (!!parameters.providerCountPages) {
          for (const providerCountPage of parameters.providerCountPages) {
            if (!providerCountPage.providerCount) {
              parameters.providerCountPages = parameters.providerCountPages.filter((x) => x.id !== providerCountPage.id);
            }
          }
        }

        // extract any unselected access criterion
        if (!!parameters.product && !!parameters.product.accessCriterion) {
          if (!parameters.product.skipAccessAnalysis) {
            parameters.product.accessCriterion = parameters.product.accessCriterion.filter((x) => x.selected);
          }

          // get latest provider set version id for each provider set
          const providerSetIds: number[] = parameters.product.accessCriterion.map(x => !!x.providerSet && x.providerSet.providerSetId);
          const providerSetVersionModels: ProviderSetVersionModel[] = await providerSetService.getLatestProviderSetVersion(providerSetIds, props.clientId);

          // get latest member set service area version id 
          let memberSetServiceAreaVersionId;
          const firstAccessCriterion: AccessReportsWizardAccessCriteria = parameters.product.accessCriterion[0];

          // all access criterion will have the same member set service area id
          if (!!firstAccessCriterion &&
            !!firstAccessCriterion.memberSetServiceArea &&
            firstAccessCriterion.memberSetServiceArea.serviceAreaId !== null &&
            firstAccessCriterion.memberSetServiceArea.serviceAreaId !== undefined &&
            !parameters.product.skipAccessAnalysis
          ) {
            const latestMemberSetServiceAreaVersionModel: ServiceAreaVersionModel = await serviceAreaService.getLatestServiceAreaVersion(
              firstAccessCriterion.memberSetServiceArea.serviceAreaId
            );

            if (!!latestMemberSetServiceAreaVersionModel) {
              memberSetServiceAreaVersionId = latestMemberSetServiceAreaVersionModel.serviceAreaVersionId;
            }
          }

          for (const accessCriteria of parameters.product.accessCriterion) {
            // attach latest provider set version id to each access criterion
            if (!!providerSetVersionModels && providerSetVersionModels.length && !!accessCriteria.providerSet) {
              const providerSetVersionModel: ProviderSetVersionModel = providerSetVersionModels.find(x => x.providerSetId === accessCriteria.providerSet.providerSetId);

              if (!!providerSetVersionModel) {
                accessCriteria.providerSet.providerSetVersionId = providerSetVersionModel.providerSetVersionId;
              }
            }

            // attach latest member set service area version id to each access criterion
            if (memberSetServiceAreaVersionId !== null && memberSetServiceAreaVersionId !== undefined && !parameters.product.skipAccessAnalysis) {
              accessCriteria.memberSetServiceArea.serviceAreaVersionId = memberSetServiceAreaVersionId;
            }

            // attach latest provider set service area version id to each access criterion
            if (!!accessCriteria.providerSetServiceArea &&
              accessCriteria.providerSetServiceArea.serviceAreaId !== null &&
              accessCriteria.providerSetServiceArea.serviceAreaId !== undefined
            ) {
              const latestProviderSetServiceAreaVersionModel: ServiceAreaVersionModel = await serviceAreaService.getLatestServiceAreaVersion(
                accessCriteria.providerSetServiceArea.serviceAreaId
              );

              if (!!latestProviderSetServiceAreaVersionModel) {
                accessCriteria.providerSetServiceArea.serviceAreaVersionId = latestProviderSetServiceAreaVersionModel.serviceAreaVersionId;
              }
            }
          }


          // attach employee group information to each member set object in each access criteria
          if (!!employeeGroup && !parameters.product.skipAccessAnalysis) {
            for (const accessCriteria of parameters.product.accessCriterion) {
              accessCriteria.memberSet.fileName = employeeGroup.fileName;
              accessCriteria.memberSet.isCensus = employeeGroup.employeeGroupType === EmployeeGroupTypeEnum.Census;
              accessCriteria.memberSet.memberSetId = employeeGroup.employeeGroupId;
              accessCriteria.memberSet.employeeGroupStatus = employeeGroup.employeeGroupStatus;
            }
          }
        }

        // assign columns to pages
        const assignColumns = (
          groupProperty: AccessReportsWizardGroupPropertyIdEnum,
          pages: AccessReportsWizardDetailsPage[] | AccessReportsWizardSummaryPage[] | AccessReportsWizardEmployeeCountPage[] | AccessListingPage[] | AccessRadiusSearchPage[] | AccessReportsWizardProviderCountPage[]
        ) => {
          const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(accessReportsWizardTemplate, AccessReportsWizardStepEnum.PageOptions, groupProperty);
          const columnsElement: AccessReportsWizardElement = pageFormGroup?.elements.find((x) => x.elementId === "columns");

          for (let page of pages) {
            // to account for clones prior to columns adjustments
            if (!page.columns?.length) {
              const columnDefaults: ColumnDefaults = pageFormGroup.columnDefaults.find(x => x.productId === parameters.product.id);
              let filter = 0;

              if (groupProperty !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && groupProperty !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
                // all non-employee count pages have an access filter that determines which columns get set
                filter = ((page as any)[columnsElement.previousRequiredElementId] as AccessReportsWizardOption)?.id;
              } else {

                switch (groupProperty) {
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
                    // employee-count specific
                    page = page as AccessReportsWizardEmployeeCountPage;

                    // attach the member set service area
                    const employeeCountAccessCriteria: AccessReportsWizardAccessCriteria = parameters.product.accessCriterion.find(x => !!x.memberSetServiceArea?.serviceAreaId);
                    page.serviceArea = employeeCountAccessCriteria?.memberSetServiceArea;

                    // also attach employee group ids
                    // at the moment, we can only upload one census file,
                    // so only attaching the sole employee group id from it
                    if (employeeGroup) {
                      page.employeeGroupIds = [employeeGroup.employeeGroupId];
                    }
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
                    // provider-count specific
                    page = page as AccessReportsWizardProviderCountPage;

                    // attach the provider set service area
                    const providerCountAccessCriteria: AccessReportsWizardAccessCriteria = parameters.product.accessCriterion.find(x => !!x.providerSetServiceArea?.serviceAreaId);
                    page.serviceArea = providerCountAccessCriteria?.providerSetServiceArea;
                    break;
                  default:
                    break;
                }

              }

              const selectedColumns: SelectedColumnsByFilter = columnDefaults?.selectedColumns.find(x => x.filterId === filter);
              const columns: AccessReportsWizardOption[] = [];

              if (!!selectedColumns) {
                for (const columnId of selectedColumns.columns) {
                  const column = columnsElement.options.find(x => x.id === columnId);

                  columns.push(column);
                }
              }

              page.columns = columns;
            }
          }
        }

        for (const groupProperty of Object.values(AccessReportsWizardGroupPropertyIdEnum)) {
          switch (groupProperty) {
            // assign string hex colors as options 
            case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
              for (const overviewPage of parameters.accessOverviewPages) {
                for (const key of Object.keys(overviewPage)) {
                  if (key.toUpperCase().includes("COLOR") && typeof (overviewPage as any)[key] === "string") {
                    (overviewPage as any)[key] = {
                      defaultProducts: [],
                      displayName: (overviewPage as any)[key],
                      hiddenProducts: [],
                      id: 1,
                      name: (overviewPage as any)[key],
                    } as AccessReportsWizardOption;
                  }
                }
              }

              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
              assignColumns(groupProperty, parameters.accessReportsWizardDetailsPages);
              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
              assignColumns(groupProperty, parameters.accessReportsWizardSummaryPages);
              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
              assignColumns(groupProperty, parameters.employeeCountPages);
              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
              assignColumns(groupProperty, parameters.accessListingPages);
              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
              assignColumns(groupProperty, parameters.accessRadiusSearchPages);
              break;
            case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
              assignColumns(groupProperty, parameters.providerCountPages);
              break;
            default:
              break;
          }
        }

        // retrieve the latest version of disclaimer
        const legalDisclaimerElement: AccessReportsWizardElement = getElementByIdFromTemplate(accessReportsWizardTemplate, AccessReportsWizardStepEnum.PageOptions, AccessReportsPageOptionsElementEnum.LegalDisclaimer);
        const legalDisclaimerOption: AccessReportsWizardOption = legalDisclaimerElement?.options.find(x => x.id === parameters.product.id);

        // the disclaimer page legal text should only be appended to the access reports model if it is defaulted to be displayed for this product
        parameters.legalDisclaimer = legalDisclaimerOption?.defaultProducts?.includes(parameters.product.id) ? legalDisclaimerOption.name : "";

        // attach print options
        parameters.printOptions = printOptions;

        // obtain shared output information
        if (!!parameters.accessSharedOutput?.to) {
          parameters.accessSharedOutput.cc = props.email;
          parameters.accessSharedOutput.bodyText = renderToStaticMarkup(<AccessReportSummary
            accessReportParameters={accessReportParameters}
            employeeGroup={employeeGroup}
          />);
        } else {
          parameters.accessSharedOutput = null;
        }
      }
    }

    const accessReportIds = await accessReportsService.createAccessReports(createAccessReportsBulkModel);
    await accessReportsService.createAccessReportsJobs({ accessReportIds });

    // redirect to dashboard
    history.push(`/access-reports`);
  }

  const updateEmployeeGroupUploadStatus = (newEmployeeGroupUploadStatus: AccessReportStatusEnum, newEmployeeGroupUploadMessage: string): void => {
    setEmployeeGroupUploadMessage(newEmployeeGroupUploadMessage);
    setEmployeeGroupUploadStatus(newEmployeeGroupUploadStatus);
  }

  const setEmployeeGroupUploadValues = (newEmployeeGroup: EmployeeGroup): void => {
    // stores an employee group after a successful census file upload
    setEmployeeGroup(newEmployeeGroup);
  }

  const setWizardStep = (newPage: number): void => {
    history.push(`/access-reports/wizard/${newPage + 1}`);
  }

  const buildDisabledButtonTooltip = (incompleteRequiredElements: AccessReportsWizardElement[]): string => {
    // creates a tooltip for the "next" button that alerts the user of incomplete required elements
    let disabledButtonTooltip = "Please select ";

    for (let i = 0; i < incompleteRequiredElements.length; i++) {
      if (i > 0) {
        if (incompleteRequiredElements.length > 2 && i < incompleteRequiredElements.length - 1) {
          disabledButtonTooltip += ", ";
        } else {
          disabledButtonTooltip += ", and ";
        }
      }

      const splitKey: string = incompleteRequiredElements[i].elementId.replace(/([A-Z])/g, " $1");
      const formattedKey: string = (splitKey.charAt(0) + splitKey.slice(1)).toLowerCase();

      disabledButtonTooltip += (formattedKey.match("^[aieouAIEOU].*") ? "an " : "a ") + formattedKey;
    }

    return disabledButtonTooltip;
  }

  const checkEmailToValidity = (emailTo: string): boolean => {
    if (!emailTo) {
      // the email to field is not required, so it being empty is perfectly valid
      return true;
    }

    // remove whitespace before assessing each email's validity
    emailTo = emailTo.replace(/\s/g, "")
    let emails: string[] = [];


    if (emailTo.includes(";")) {
      emails = emailTo.split(";");
    } else {
      emails = [emailTo];
    }

    for (const email of emails) {
      // this is utilizing the same email validity found in AdminUsersContext.tsx
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const valid = re.test(email.replace(/(-Disable)$/, "").toLowerCase());

      if (!valid) {
        return false;
      }
    }

    return true;
  }

  const checkReportTitleValidity = (reportTitle: string): boolean => {
    // returns whether a report title string contains the following "invalid" characters:  / \ : . * ? " < > |
    return !/[/\\:.*\\?"<>|]/.test(reportTitle);
  }

  const createWizardStep = (
    wizardStep: AccessReportsWizardStepEnum,
    currentWizardStep: number,
    nextPath?: string,
    onSubmitClick?: () => void,
    previousPath?: string,
  ) => {
    const skipAccessAnalysis = accessReportParameters?.product?.skipAccessAnalysis;
    let nextPathDisabled = false;
    let nextPathDisabledTooltipText = "";
    let redirect = false;
    let step: React.ReactNode = null;

    if (!isLoading) {
      switch (wizardStep) {
        case AccessReportsWizardStepEnum.CoverPage:
          const coverPageFormGroups: AccessReportsWizardFormGroup[] = getFormGroups(AccessReportsWizardStepEnum.CoverPage);
          const incompleteRequiredCoverPageElements: AccessReportsWizardElement[] = getIncompleteRequiredElements(AccessReportsWizardStepEnum.CoverPage);
          const coverPageTemplate: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);
          const coverPageUnrequiredValidationElements = [
            AccessReportsCoverPageElementEnum.ReportTitleOne,
            AccessReportsCoverPageElementEnum.ReportTitleTwo,
            AccessReportsCoverPageElementEnum.ReportTitleThree,
          ];
          nextPathDisabled = incompleteRequiredCoverPageElements.length > 0 || !accessReportsWizardTemplate || !coverPageTemplate || invalidElements.size > 0;

          if (!accessReportsWizardTemplate || !coverPageTemplate) {
            nextPathDisabledTooltipText = "A template has not been loaded";
          } else if (incompleteRequiredCoverPageElements.length > 0) {
            nextPathDisabledTooltipText = buildDisabledButtonTooltip(incompleteRequiredCoverPageElements);
          } else if (invalidElements.size > 0) {
            nextPathDisabledTooltipText = `Please correct the fields with invalid values`;
          }

          const shareOutputFormGroup: AccessReportsWizardFormGroup = coverPageFormGroups.find(x => x.elements.find(y => y.elementId === AccessReportsCoverPageElementEnum.To));
          const toElement: AccessReportsWizardElement = shareOutputFormGroup.elements.find(x => x.elementId === AccessReportsCoverPageElementEnum.To);

          const getCoverPageForm = (formGroups: AccessReportsWizardFormGroup[], unrequiredValidationElements: string[]): JSX.Element => {
            return (
              <AccessReportsWizardForm
                accessReportParameters={accessReportParameters}
                formGroups={formGroups}
                getDisabledStatus={getDisabledStatus}
                invalidElements={invalidElements}
                invalidPageOptionPageElements={invalidPageOptionPageElements}
                onCheckboxChange={onCheckboxChange}
                onAddPageClick={onAddPageClick}
                onInputChange={onInputChange}
                onRemovePageClick={onRemovePageClick}
                onSelectChange={onSelectChange}
                repeatedByProductDefaultIds={repeatedByProductDefaultIds}
                unrequiredValidationElements={unrequiredValidationElements}
                wizardStep={AccessReportsWizardStepEnum.CoverPage}
              />
            );
          }

          step = (
            <React.Fragment>
              {showProductDoesntExistBanner && (
                <QESAlert classNames="mb-3" type="danger" persist={true}>
                  The product you attempted to clone no longer exists.
                  You may still run this report, but it is not advised.
                </QESAlert>
              )}
              {getCoverPageForm(coverPageFormGroups.filter(x => x.id !== shareOutputFormGroup.id), coverPageUnrequiredValidationElements)}
              {!!toElement && !toElement.hidden && (
                <React.Fragment>
                  <div className="row">
                    <div className="col-12">
                      <p className="copy-text-default">
                        The report output will be available to you in the Access Report Dashboard. Optionally, if you wish to have the report emailed to a recipient or recipients, with a copy emailed to you, enter the recipient&#39;s email address below. Separate multiple email addresses with a semicolon.
                      </p>
                    </div>
                  </div>
                  {getCoverPageForm([shareOutputFormGroup], [AccessReportsCoverPageElementEnum.To])}
                </React.Fragment>
              )}
            </React.Fragment>
          );
          break;
        case AccessReportsWizardStepEnum.AccessAnalysis:
          if (!!accessReportParameters.product && !!accessReportParameters.productType) {
            const accessAnalysisFormGroups: AccessReportsWizardFormGroup[] = getFormGroups(AccessReportsWizardStepEnum.AccessAnalysis);
            const accessAnalysisWizardForm: React.ReactNode = (
              <AccessReportsWizardForm
                accessReportParameters={accessReportParameters}
                formGroups={accessAnalysisFormGroups}
                getDisabledStatus={getDisabledStatus}
                invalidElements={invalidElements}
                invalidPageOptionPageElements={invalidPageOptionPageElements}
                onAddPageClick={onAddPageClick}
                onCheckboxChange={onCheckboxChange}
                onInputChange={onInputChange}
                onRemovePageClick={onRemovePageClick}
                onSelectChange={onSelectChange}
                repeatedByProductDefaultIds={repeatedByProductDefaultIds}
                unrequiredValidationElements={[]}
                wizardStep={AccessReportsWizardStepEnum.AccessAnalysis}
              />
            );

            const accessAnalysisTemplate: AccessReportsWizardPage = accessReportsWizardTemplate && accessReportsWizardTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.AccessAnalysis);
            const incompleteRequiredAccessAnalysisElements: AccessReportsWizardElement[] = getIncompleteRequiredElements(AccessReportsWizardStepEnum.AccessAnalysis);
            const selectedAccessCriterion: AccessReportsWizardAccessCriteria[] = (accessReportParameters && accessReportParameters.product && accessReportParameters.product.accessCriterion) ?
              accessReportParameters.product.accessCriterion.filter((x) => x.selected) :
              [];

            if (!accessReportsWizardTemplate || !accessAnalysisTemplate) {
              nextPathDisabled = true;
              nextPathDisabledTooltipText = "A template has not been loaded";
            } else if (incompleteRequiredAccessAnalysisElements.length > 0) {
              nextPathDisabled = true;
              nextPathDisabledTooltipText = buildDisabledButtonTooltip(incompleteRequiredAccessAnalysisElements);
            } else if (selectedAccessCriterion.length < 1) {
              nextPathDisabled = true;
              nextPathDisabledTooltipText = "Please select at least one access criteria";
            }

            step = (
              <AccessReportsWizardStepAccessAnalysis
                accessAnalysisWizardForm={accessAnalysisWizardForm}
                accessReportParameters={accessReportParameters}
                accessReportsWizardTemplate={accessReportsWizardTemplate}
                onAccessCriteriaCheckboxToggle={onAccessCriteriaCheckboxToggle}
                onGroupedAccessCriterionClick={onGroupedAccessCriterionClick}
                onProductChange={onProductChange}
              />
            );
          } else {
            redirect = true;
          }

          break;
        case AccessReportsWizardStepEnum.Population:
          if (!!accessReportParameters.product &&
            !!accessReportParameters.productType &&
            !!accessReportParameters.standard &&
            !!accessReportParameters.calculationMethod &&
            !!accessReportParameters.distanceMethod) {
            const populationTemplate: AccessReportsWizardPage = accessReportsWizardTemplate && accessReportsWizardTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.Population);
            nextPathDisabled = !employeeGroup || !accessReportsWizardTemplate || !populationTemplate || employeeGroupUploadStatus !== AccessReportStatusEnum.Succeeded;

            if (!accessReportsWizardTemplate || !populationTemplate) {
              nextPathDisabledTooltipText = "A template has not been loaded";
            } else if (!employeeGroupUploadStatus) {
              nextPathDisabledTooltipText = "Please upload a census file";
            } else if (employeeGroupUploadStatus !== AccessReportStatusEnum.Succeeded || !employeeGroup) {
              nextPathDisabledTooltipText = "Please allow for your file to process successfully";
            }

            step = (
              <AccessReportsWizardStepPopulation
                employeeGroup={employeeGroup}
                employeeGroupExamples={employeeGroupExamples}
                employeeGroupUploadMessage={employeeGroupUploadMessage}
                employeeGroupUploadStatus={employeeGroupUploadStatus}
                setEmployeeGroupUploadStatus={updateEmployeeGroupUploadStatus}
                setEmployeeGroupUploadValues={setEmployeeGroupUploadValues}
              />
            );
          } else {
            redirect = true;
          }

          break;
        case AccessReportsWizardStepEnum.PageOptions:
          if (!!accessReportParameters.product &&
            !!accessReportParameters.productType &&
            ((!skipAccessAnalysis && !!accessReportParameters.standard) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!accessReportParameters.calculationMethod) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!accessReportParameters.distanceMethod) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!employeeGroup) || skipAccessAnalysis)) {
            const pageOptionsFormGroups: AccessReportsWizardFormGroup[] = getFormGroups(AccessReportsWizardStepEnum.PageOptions);
            const incompleteRequiredPageOptionsElements: AccessReportsWizardElement[] = getIncompleteRequiredElements(AccessReportsWizardStepEnum.PageOptions);
            const pageOptionsTemplate: AccessReportsWizardPage = accessReportsWizardTemplate && accessReportsWizardTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.PageOptions);
            nextPathDisabled = incompleteRequiredPageOptionsElements.length > 0 || !accessReportsWizardTemplate || !pageOptionsTemplate;
            nextPathDisabledTooltipText = (!accessReportsWizardTemplate || !pageOptionsTemplate) ? "A template has not been loaded" : buildDisabledButtonTooltip(incompleteRequiredPageOptionsElements);

            step = (
              <AccessReportsWizardForm
                accessReportParameters={accessReportParameters}
                formGroups={pageOptionsFormGroups}
                getDisabledStatus={getDisabledStatus}
                invalidElements={invalidElements}
                invalidPageOptionPageElements={invalidPageOptionPageElements}
                onAddPageClick={onAddPageClick}
                onCheckboxChange={onCheckboxChange}
                onInputChange={onInputChange}
                onRemovePageClick={onRemovePageClick}
                onSelectChange={onSelectChange}
                repeatedByProductDefaultIds={repeatedByProductDefaultIds}
                unrequiredValidationElements={[
                  AccessRadiusSearchPageElementEnum.RadiiOne,
                  AccessRadiusSearchPageElementEnum.RadiiTwo,
                  AccessRadiusSearchPageElementEnum.RadiiThree,
                  AccessRadiusSearchPageElementEnum.RadiiFour,
                  AccessRadiusSearchPageElementEnum.RadiiFive,
                ]}
                wizardStep={AccessReportsWizardStepEnum.PageOptions}
              />
            );
          } else {
            redirect = true;
          }

          break;
        case AccessReportsWizardStepEnum.Review:
          if (!!accessReportParameters.product &&
            !!accessReportParameters.productType &&
            ((!skipAccessAnalysis && !!accessReportParameters.standard) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!accessReportParameters.calculationMethod) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!accessReportParameters.distanceMethod) || skipAccessAnalysis) &&
            ((!skipAccessAnalysis && !!employeeGroup) || skipAccessAnalysis)) {
            step = (
              <React.Fragment>
                <div className="row">
                  <div className="col-12">
                    <p className="copy-text-default">
                      Please review the details below outlining the options you have requested for your Access Analysis Report...
                    </p>
                  </div>
                </div>

                <AccessReportSummary
                  accessReportParameters={accessReportParameters}
                  employeeGroup={employeeGroup}
                />
              </React.Fragment>
            );
          } else {
            redirect = true;
          }

          break;
        default:
          break;
      }
    } else {
      step = (
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      );
    }

    if (redirect) {
      return <Redirect to={`/access-reports/wizard/1`} />;
    } else {
      const hiddenWizardSteps: AccessReportsWizardStepEnum[] = [
        AccessReportsWizardStepEnum.PrintOptions,
        AccessReportsWizardStepEnum.MultiElementOptions,
        accessReportParameters?.product?.skipAccessAnalysis && AccessReportsWizardStepEnum.AccessAnalysis,
        accessReportParameters?.product?.skipAccessAnalysis && AccessReportsWizardStepEnum.Population,
      ];

      return (
        <AccessReportsWizardStep
          currentWizardStep={currentWizardStep}
          nextPath={nextPath}
          nextPathDisabled={isLoading ? true : nextPathDisabled}
          nextPathDisabledTooltipText={nextPathDisabledTooltipText}
          onSubmitClick={onSubmitClick}
          previousPath={previousPath}
          setWizardStep={setWizardStep}
          wizardSteps={Object.values(AccessReportsWizardStepEnum).filter(x => !hiddenWizardSteps.includes(x))}
        >
          {step}
        </AccessReportsWizardStep>
      );
    }
  }

  const extractDefaultValues = (
    accessReportsWizardTemplate: AccessReportsWizardTemplate
  ): AccessReportParametersModel => {
    // the purpose of this function is to obtain a "blank slate,"
    // then proceed to fill in any defaults that are more generic,
    // meaning these defaults are NOT at a per product level
    const newAccessReportParameters: AccessReportParametersModel = getCleanAccessReportParameters(accessReportParameters);

    if (!!accessReportsWizardTemplate.pages && accessReportsWizardTemplate.pages.length) {
      for (const page of accessReportsWizardTemplate.pages) {
        if (!!page.formGroups) {
          for (const formGroup of page.formGroups) {
            // form groups with group property ids ("pages")
            // will not have generic default values attached to them,
            // as default "pages" will be created at a per product level
            if (!formGroup.groupPropertyId && !!formGroup.elements) {
              for (const element of formGroup.elements) {
                if ((isClone && !Object.values(AccessReportsProductDefaultElementsEnum).includes(element.elementId as any)) || !isClone) {
                  if (element.elementId in newAccessReportParameters && element.defaultValue !== undefined && element.defaultValue !== null) {
                    if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                      const option: AccessReportsWizardOption = element.options.find((x) => x.id.toString() === element.defaultValue.toString());

                      if (!!option) {
                        (newAccessReportParameters as any)[element.elementId] = option;
                      }
                    } else {
                      (newAccessReportParameters as any)[element.elementId] = element.defaultValue;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return newAccessReportParameters;
  }

  const fetchAccessReportsWizardTemplateAndModel = async (): Promise<void> => {
    const accessReportsWizard: AccessReportsWizardModel = await accessReportsService.getAccessReportsWizard();

    const latestVersion: AccessReportsWizardVersionModel = await accessReportsService.getLatestAccessReportsWizardVersion(
      accessReportsWizard.accessReportWizardId,
      true,
    );

    const newOriginalAccessReportsWizardTemplate: AccessReportsWizardTemplate = assignTemplateMultiElementOptions(latestVersion.template);
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = cloneDeep(newOriginalAccessReportsWizardTemplate);
    const routeState = (location.state || {}) as RouteState;
    const newIsClone: boolean = (routeState && routeState.accessReportId !== null && routeState.accessReportId !== undefined) ? true : false;

    let newAccessReportParameters: AccessReportParametersModel = null;
    let newEmployeeGroup: EmployeeGroup = null;
    let newEmployeeGroupUploadStatus: AccessReportStatusEnum = null;
    let newShowProductDoesntExistBanner = false;

    if (newIsClone) {
      const accessReport: AccessReport = await accessReportsService.getAccessReport(routeState.accessReportId);

      if (!!accessReport?.parameters) {
        newAccessReportParameters = accessReport.parameters;

        const productTypeElement: AccessReportsWizardElement = getElementByIdFromTemplate(newAccessReportsWizardTemplate, AccessReportsWizardStepEnum.CoverPage, AccessReportsCoverPageElementEnum.ProductType);
        const productElement: AccessReportsWizardElement = getElementByIdFromTemplate(newAccessReportsWizardTemplate, AccessReportsWizardStepEnum.CoverPage, AccessReportsCoverPageElementEnum.Product);
        const productTypeOption: AccessReportsWizardOption = productTypeElement?.options.find(x => x.id === newAccessReportParameters.productType.id);
        const productOption: AccessReportsWizardOption = productElement?.options.find(x => x.id === newAccessReportParameters.product.id);

        // if a product OR product type with a corresponding id are no longer in the template:
        // 1.) present a banner to the user explaining the situation
        // 2.) still expose the selected the options to the user
        // 3.) disable both the product and product type input boxes
        if (!productOption || !productTypeOption) {
          // product
          newAccessReportParameters.product.previousElementFilterId = newAccessReportParameters.productType.id;
          newAccessReportParameters.product.hiddenProducts = [];
          productElement.options = [newAccessReportParameters.product];
          productElement.disabled = true;

          // product type
          newAccessReportParameters.productType.hiddenProducts = [];
          productTypeElement.options = [newAccessReportParameters.productType];
          productTypeElement.disabled = true;

          newShowProductDoesntExistBanner = true;
        }

        if (!newAccessReportParameters.accessSharedOutput) {
          newAccessReportParameters.accessSharedOutput = {
            bodyText: "",
            cc: "",
            to: "",
          };
        }

        const pageOptionsPage: AccessReportsWizardPage = newAccessReportsWizardTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.PageOptions);

        if (!!pageOptionsPage) {
          for (const groupProperty of Object.values(AccessReportsWizardGroupPropertyIdEnum)) {
            const pageFormGroup = pageOptionsPage.formGroups.find(x => x.groupPropertyId === groupProperty);

            if (newAccessReportParameters[groupProperty]?.length) {
              let id = 0;

              for (const page of newAccessReportParameters[groupProperty]) {
                const pageEnumSelectionKey: string = Object.keys(PageSelectionElementIdEnum).find(x => x.toUpperCase() === groupProperty.toUpperCase())
                const pageSelectionKey = (PageSelectionElementIdEnum as any)[pageEnumSelectionKey];
                (page as any)[pageSelectionKey] = true;
                page.id = id;
                id = id + 1;
              }
            } else {
              // if the clone did not have any of this page type selected, but the template still allows for this page type to be included
              if (!pageFormGroup.hiddenProducts.includes(newAccessReportParameters.product.id)) {
                const templateByProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === newAccessReportParameters.product.id);

                // stored defaults from the template
                if (templateByProductDefaults.length) {
                  for (const byProductDefault of templateByProductDefaults) {
                    for (const element of pageFormGroup.elements) {
                      if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                        const option: AccessReportsWizardOption = element.options.find(x => x.id === byProductDefault[element.elementId]);

                        if (!!option) {
                          byProductDefault[element.elementId] = option;
                        }
                      }
                    }

                    const pageEnumSelectionKey: string = Object.keys(PageSelectionElementIdEnum).find(x => x.toUpperCase() === groupProperty.toUpperCase())
                    const pageSelectionKey = (PageSelectionElementIdEnum as any)[pageEnumSelectionKey];
                    (byProductDefault as any)[pageSelectionKey] = false;
                  }

                  (newAccessReportParameters[groupProperty] as any) = templateByProductDefaults;
                } else {
                  // if the template does not have defaults stored for this page type,
                  // but they still have the ability to add the page
                  const id: number = pageFormGroup.byProductDefaults.length > 0 ? Math.max(...pageFormGroup.byProductDefaults.map((x) => x.id)) + 1 : 0;
                  const page = createPage(groupProperty, id, false, newAccessReportsWizardTemplate);

                  (newAccessReportParameters[groupProperty] as any).push(page);
                }
              }
            }
          }
        }

        // make sure to include ALL the access criterion as options,
        // not just what was selected in the previous access report
        if (!!newAccessReportsWizardTemplate) {
          newEmployeeGroup = includeAllAccessCriterion(newAccessReportParameters, newEmployeeGroup, newAccessReportsWizardTemplate);

          if (!!newEmployeeGroup) {
            newEmployeeGroupUploadStatus = AccessReportStatusEnum.Succeeded;
          }
        }
      }
    } else {
      newAccessReportParameters = extractDefaultValues(newAccessReportsWizardTemplate);
    }

    previousAccessReportParameters.current = accessReportParameters;
    previousIsClone.current = isClone;
    setAccessReportParameters(newAccessReportParameters);
    setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
    setEmployeeGroup(newEmployeeGroup);
    setEmployeeGroupUploadStatus(newEmployeeGroupUploadStatus);
    setIsClone(newIsClone);
    setIsLoading(false);
    setOriginalAccessReportsWizardTemplate(newOriginalAccessReportsWizardTemplate);
    setShowProductDoesntExistBanner(newShowProductDoesntExistBanner);
  }

  const includeAllAccessCriterion = (
    accessReportParameters: AccessReportParametersModel,
    employeeGroup: EmployeeGroup,
    template: AccessReportsWizardTemplate,
  ): EmployeeGroup => {
    const coverPage: AccessReportsWizardPage = template.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);
    const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);
    const accessReportsWizardProducts: AccessReportsWizardProduct[] = (productElement?.options as any) as AccessReportsWizardProduct[];
    const product: AccessReportsWizardProduct = accessReportsWizardProducts.find((x) => x.id === accessReportParameters.product.id);

    // after obtaining all of the possible access criteria options,
    // make sure to select all of the previously-selected ones
    if (!!product) {
      for (const accessCriteria of accessReportParameters.product.accessCriterion) {
        const newAccessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find(x => x.id === accessCriteria.id);

        if (!!newAccessCriteria) {
          newAccessCriteria.selected = true;
        }

        if (!!accessCriteria.memberSet && !employeeGroup) {
          employeeGroup = {
            clientId: null,
            dateCreated: null,
            employeeGroupId: accessCriteria.memberSet.memberSetId,
            employeeGroupStatus: accessCriteria.memberSet.employeeGroupStatus,
            employeeGroupType: accessCriteria.memberSet.isCensus ? EmployeeGroupTypeEnum.Census : null,
            fileName: accessCriteria.memberSet.fileName,
            userId: null,
          }
        }
      }

      accessReportParameters.product = product;
    }

    return employeeGroup;
  }

  const getEmployeeGroupExamples = async (): Promise<void> => {
    const newEmployeeGroupExamples: EmployeeGroupExample[] = await accessReportsService.getEmployeeGroupExamples();

    if (!!newEmployeeGroupExamples) {
      setEmployeeGroupExamples(newEmployeeGroupExamples);
    }
  }

  const getFormGroups = (wizardStep: AccessReportsWizardStepEnum): AccessReportsWizardFormGroup[] => {
    let formGroups: AccessReportsWizardFormGroup[] = [];

    if (!!accessReportsWizardTemplate && !!accessReportsWizardTemplate.pages) {
      const page: AccessReportsWizardPage = accessReportsWizardTemplate.pages.find((x) => x.title === wizardStep);

      if (!!page) {
        formGroups = page.formGroups;
      }
    }

    return formGroups;
  }

  const getIncompleteRequiredElements = (wizardStep: AccessReportsWizardStepEnum): AccessReportsWizardElement[] => {
    // obtaining a list of elements that are marked as required (but are not currently disabled) and do not have values
    const incompleteRequiredElements: AccessReportsWizardElement[] = [];
    let formGroups: AccessReportsWizardFormGroup[] = [];

    if (!!accessReportsWizardTemplate) {
      const page: AccessReportsWizardPage = getPageFromTemplate(accessReportsWizardTemplate, wizardStep);

      if (!!page) {
        formGroups = page.formGroups;
      }
    }

    for (const formGroup of formGroups) {
      if (!!formGroup.groupPropertyId) {
        const formGroupModels = (accessReportParameters as any)[formGroup.groupPropertyId];

        for (const formGroupModel of formGroupModels) {
          for (const element of formGroup.elements) {
            const disabled: boolean = getDisabledStatus(formGroup, element, formGroupModel.id);
            const hasValue: boolean = !!formGroupModel[element.elementId] ? true : false;

            if (element.required && !formGroup.hiddenProducts.includes(accessReportParameters.product.id) && !element.hidden && !disabled && !hasValue) {
              incompleteRequiredElements.push(element);
            }
          }
        }
      } else {
        for (const element of formGroup.elements) {
          const disabled: boolean = getDisabledStatus(formGroup, element);
          const hasValue: boolean = !!(accessReportParameters as any)[element.elementId] ? true : false;

          if (element.required && !element.hidden && !disabled && !hasValue) {
            incompleteRequiredElements.push(element);
          }
        }
      }
    }

    return incompleteRequiredElements;
  }

  const resetDefaultsForNewProductType = () => {
    const newAccessReportParameters: AccessReportParametersModel = extractDefaultValues(originalAccessReportsWizardTemplate);
    newAccessReportParameters.productType = accessReportParameters.productType;

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
  }

  const setDefaultsByProduct = (): void => {
    // whenever a new product is selected, we will want to reset everything to null values,
    // (in order to clear out the previously-selected product's defaults)
    // followed by extracting defaults that are not specific to a product
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = cloneDeep(originalAccessReportsWizardTemplate);
    const newAccessReportParameters: AccessReportParametersModel = extractDefaultValues(originalAccessReportsWizardTemplate);
    newAccessReportParameters.productType = accessReportParameters.productType;
    newAccessReportParameters.product = accessReportParameters.product;

    if (!!newAccessReportsWizardTemplate && !!newAccessReportParameters && !!newAccessReportParameters.product) {
      for (const page of newAccessReportsWizardTemplate.pages) {
        const ungroupedFormGroups: AccessReportsWizardFormGroup[] = page.formGroups.filter(x => !x.groupPropertyId);
        const elements: AccessReportsWizardElement[] = flatten(ungroupedFormGroups.map(x => x.elements));

        for (const element of elements) {
          if (!!element) {
            switch (element.elementType) {
              case AccessReportsWizardElementTypeEnum.Input:
                const defaultInputOption: AccessReportsWizardOption = !!element.options && element.options.find(x => x.id === newAccessReportParameters.product.id);

                if (!!defaultInputOption) {
                  (newAccessReportParameters as any)[element.elementId] = defaultInputOption.displayName ? defaultInputOption.displayName : defaultInputOption.name;
                }

                break;
              case AccessReportsWizardElementTypeEnum.Checkbox:
                const defaultCheckboxOption: AccessReportsWizardOption = !!element.options && element.options.find(x => x.id === newAccessReportParameters.product.id);

                if (!!defaultCheckboxOption && !!defaultCheckboxOption.defaultProducts && defaultCheckboxOption.defaultProducts.includes(newAccessReportParameters.product.id)) {
                  (newAccessReportParameters as any)[element.elementId] = true;
                }

                break;
              case AccessReportsWizardElementTypeEnum.Dropdown:
                const defaultDropdownOption: AccessReportsWizardOption = !!element.options && element.options.find(x => !!x.defaultProducts && x.defaultProducts.includes(newAccessReportParameters.product.id));

                if (newAccessReportParameters.product.skipAccessAnalysis && Object.values(AccessReportsAccessAnalysisElementEnum).includes(element.elementId as AccessReportsAccessAnalysisElementEnum)) {
                  (newAccessReportParameters as any)[element.elementId] = null;
                } else {
                  if (!!defaultDropdownOption) {
                    (newAccessReportParameters as any)[element.elementId] = defaultDropdownOption;
                  }
                }

                break;
              default:
                break;
            }

            if (element.elementId === AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard) {
              let isHiddenByProduct = false;

              if (!!element.options) {
                const defaultOption: AccessReportsWizardOption = element.options.find(
                  x => x.id === newAccessReportParameters.product.id
                );

                if (!!defaultOption && !!defaultOption.hiddenProducts && defaultOption.hiddenProducts.includes(newAccessReportParameters.product.id)) {
                  isHiddenByProduct = true;
                }
              }

              // only display the checkbox if the access analysis is not standard,
              // and only make it visible if the "split employee group by standard" checkbox
              // is marked as visible by the admin tool for the currently-selected product
              if (newAccessReportParameters.standard &&
                newAccessReportParameters.standard.id !== AccessReportsWizardAnalysisTypeEnum.Standard &&
                !isHiddenByProduct
              ) {
                element.hidden = false;
              } else {
                element.hidden = true;
                newAccessReportParameters.splitEmployeeGroupByStandard = false;
              }
            }
          }
        }

        if (page.title === AccessReportsWizardStepEnum.PageOptions) {
          for (const groupProperty of Object.values(AccessReportsWizardGroupPropertyIdEnum)) {
            const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newAccessReportsWizardTemplate, AccessReportsWizardStepEnum.PageOptions, groupProperty);

            if (!pageFormGroup.hiddenProducts.includes(newAccessReportParameters.product.id)) {
              const byProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === newAccessReportParameters.product.id);

              // if this page type does not have any defaults stored but it is not set to be hidden
              if (!byProductDefaults?.length) {
                const id: number = pageFormGroup.byProductDefaults.length > 0 ? (Math.max(...pageFormGroup.byProductDefaults.map((x) => x.id)) + 1) : 0;
                const page = createPage(groupProperty, id, false);
                (newAccessReportParameters[groupProperty] as any).push(page);

                (page as any).productId = accessReportParameters.product.id;
                const byProductDefault: ByProductDefault = page as any;
                byProductDefaults.push(byProductDefault);
              }

              const setOptions = (byProductDefault: ByProductDefault) => {
                for (const element of pageFormGroup.elements) {
                  if (element.elementType === AccessReportsWizardElementTypeEnum.Dropdown) {
                    const option: AccessReportsWizardOption = element.options.find(x => x.id === byProductDefault[element.elementId]);

                    if (!!option) {
                      byProductDefault[element.elementId] = option;
                    }
                  }
                }
              }

              for (const byProductDefault of byProductDefaults) {
                setOptions(byProductDefault);

                switch (groupProperty) {
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
                    newAccessReportParameters.accessReportsWizardSummaryPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardDetailsPages:
                    newAccessReportParameters.accessReportsWizardDetailsPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
                    newAccessReportParameters.employeeCountPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
                    newAccessReportParameters.accessOverviewPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessListingPages:
                    newAccessReportParameters.accessListingPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch:
                    newAccessReportParameters.accessRadiusSearchPages.push(byProductDefault as any);
                    break;
                  case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
                    newAccessReportParameters.providerCountPages.push(byProductDefault as any);
                    break;
                  default:
                    break;
                }
              }
            }
          }
        }
      }

      previousAccessReportParameters.current = accessReportParameters;
      setAccessReportParameters(newAccessReportParameters);
      setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
      setProductChanged(true);
    }
  }

  const updateCountSettings = (groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[]): void => {
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = cloneDeep(accessReportsWizardTemplate);
    const page: AccessReportsWizardPage = newAccessReportsWizardTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.PageOptions);

    for (const groupPropertyId of groupPropertyIds) {
      // check if any access criterion have a member set service area attached to them
      let accessCriteriaWithServiceArea: AccessReportsWizardAccessCriteria;
      let pages: AccessReportsWizardProviderCountPage[] | AccessReportsWizardEmployeeCountPage[] = [];

      switch (groupPropertyId) {
        case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage:
          accessCriteriaWithServiceArea = newAccessReportParameters.product.accessCriterion.find(x => !!x.memberSetServiceArea?.serviceAreaId);
          pages = newAccessReportParameters.employeeCountPages;
          break;
        case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages:
          accessCriteriaWithServiceArea = newAccessReportParameters.product.accessCriterion.find(x => !!x.providerSetServiceArea?.serviceAreaId);
          pages = newAccessReportParameters.providerCountPages;
          break;
        default:
          break;
      }

      // update the template accordingly
      if (!!page) {
        const formGroups: AccessReportsWizardFormGroup[] = page.formGroups.filter((x) => x.groupPropertyId === groupPropertyId);

        if (formGroups.length) {
          for (const formGroup of formGroups) {
            for (const element of formGroup.elements) {
              if (element.elementId === "filteredWithinServiceArea") {
                if (!!accessCriteriaWithServiceArea) {
                  element.hidden = false;
                } else {
                  element.hidden = true;
                }
              }
            }
          }
        }
      }

      // update the model accordingly
      for (const p of pages) {
        if (accessCriteriaWithServiceArea) {
          p.hasServiceArea = true;
        } else {
          p.hasServiceArea = false;
        }
      }
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
    setProductChanged(true);
  }

  const updateSplitEmployeeGroupByStandardSettings = (): void => {
    const newAccessReportsWizardTemplate: AccessReportsWizardTemplate = cloneDeep(accessReportsWizardTemplate);
    const newAccessReportParameters: AccessReportParametersModel = cloneDeep(accessReportParameters);

    if (!!newAccessReportsWizardTemplate && !!newAccessReportParameters) {
      const pageOptionsPage: AccessReportsWizardPage = newAccessReportsWizardTemplate.pages.find(
        (x) => x.title === AccessReportsWizardStepEnum.PageOptions
      );

      if (!!pageOptionsPage) {
        const pageOptionsPageElements: AccessReportsWizardElement[] = flatten(pageOptionsPage.formGroups.map(x => x.elements));

        if (pageOptionsPageElements.length) {
          const splitEmployeeGroupByStandard: AccessReportsWizardElement = pageOptionsPageElements.find(
            x => x.elementId === AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard
          );

          if (!!splitEmployeeGroupByStandard) {
            let isHiddenByProduct = false;

            if (!!newAccessReportParameters.product) {
              if (!!splitEmployeeGroupByStandard.options) {
                const defaultOption: AccessReportsWizardOption = splitEmployeeGroupByStandard.options.find(
                  x => x.id === newAccessReportParameters.product.id
                );

                if (!!defaultOption && !!defaultOption.hiddenProducts && defaultOption.hiddenProducts.includes(newAccessReportParameters.product.id)) {
                  isHiddenByProduct = true;
                }
              }
            }

            // only display the checkbox if the access analysis is not standard,
            // and only make it visible if the "split employee group by standard" checkbox
            // is marked as visible by the admin tool for the currently-selected product
            if (newAccessReportParameters.standard &&
              newAccessReportParameters.standard.id !== AccessReportsWizardAnalysisTypeEnum.Standard &&
              newAccessReportParameters.product &&
              !isHiddenByProduct
            ) {
              splitEmployeeGroupByStandard.hidden = false;
            } else {
              splitEmployeeGroupByStandard.hidden = true;
              newAccessReportParameters.splitEmployeeGroupByStandard = false;
            }
          }
        }
      }
    }

    previousAccessReportParameters.current = accessReportParameters;
    setAccessReportParameters(newAccessReportParameters);
    setAccessReportsWizardTemplate(newAccessReportsWizardTemplate);
    setProductChanged(false);
  };

  const skipAccessAnalysis = accessReportParameters?.product?.skipAccessAnalysis;

  return (
    <Switch>
      <Route
        exact={true}
        path={`${routeMatch.url}/1`}
        render={() => createWizardStep(AccessReportsWizardStepEnum.CoverPage, 0, "/access-reports/wizard/2")}
      />
      {!skipAccessAnalysis && (
        <Route
          exact={true}
          path={`${routeMatch.url}/2`}
          render={() => createWizardStep(AccessReportsWizardStepEnum.AccessAnalysis, 1, "/access-reports/wizard/3", null, "/access-reports/wizard/1")}
        />
      )}
      {!skipAccessAnalysis && (
        <Route
          exact={true}
          path={`${routeMatch.url}/3`}
          render={() => createWizardStep(AccessReportsWizardStepEnum.Population, 2, "/access-reports/wizard/4", null, "/access-reports/wizard/2")}
        />
      )}
      <Route
        exact={true}
        path={`${routeMatch.url}/${skipAccessAnalysis ? "2" : "4"}`}
        render={() => createWizardStep(AccessReportsWizardStepEnum.PageOptions, skipAccessAnalysis ? 1 : 3, `/access-reports/wizard/${skipAccessAnalysis ? "3" : "5"}`, null, `/access-reports/wizard/${skipAccessAnalysis ? "1" : "3"}`)}
      />
      <Route
        exact={true}
        path={`${routeMatch.url}/${skipAccessAnalysis ? "3" : "5"}`}
        render={() => createWizardStep(AccessReportsWizardStepEnum.Review, skipAccessAnalysis ? 2 : 4, null, onSubmitClick, `/access-reports/wizard/${skipAccessAnalysis ? "2" : "4"}`)}
      />
    </Switch>
  );
}

export { AccessReportsWizard };
